<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { vMaska } from 'maska/vue';
  import type { FormElement, RegisterNumberInputProperties } from '../../../model/form.types';
  import { useLabelPosition } from '../../../model/useLabelPosition';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import { useEditableField } from '../../../model/useFormBuilderReadonly';
  import { MASK_PRESETS } from '@/shared/lib/mask';
  import { Tooltip } from '@/shared/ui';

  interface Props {
    element: FormElement & { properties: RegisterNumberInputProperties };
  }

  const props = defineProps<Props>();

  const inputValue = ref('');

  const { isLabelLeft, inputClasses, labelClasses, labelStyles } = useLabelPosition(
    computed(() => props.element.properties)
  );

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'REGISTER_NUMBER_INPUT'
  );

  const maskConfig = computed(() => ({
    mask: MASK_PRESETS.register_number,
    eager: false,
  }));

  const isEditable = useEditableField(() => props.element.properties.editable);

  const placeholderText = computed(() => props.element.properties.placeholder);

  const logFieldNameAttr = computed(() => props.element.properties.logFieldName || undefined);
</script>

<template>
  <div
    class="register-number-input-element"
    :class="{ 'label-left': isLabelLeft }"
    :style="containerStyles"
  >
    <component
      :is="element.properties.tooltip && element.properties.label ? Tooltip : 'span'"
      :content="
        element.properties.tooltip && element.properties.label
          ? element.properties.tooltip
          : undefined
      "
    >
      <label
        :for="`input-${element.id}`"
        :class="labelClasses"
        :style="labelStyles"
      >
        {{ element.properties.label }}
      </label>
    </component>
    <input
      :id="`input-${element.id}`"
      v-model="inputValue"
      v-maska="maskConfig"
      type="text"
      :name="element.properties.name"
      :placeholder="placeholderText"
      :required="element.properties.required"
      :aria-label="element.properties.label || element.properties.name"
      :readonly="!isEditable"
      :class="inputClasses"
      :data-log-field-name="logFieldNameAttr"
    >
  </div>
</template>

<style scoped>
  .register-number-input-element {
    font-family: var(--font-base);
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .register-number-input-element.label-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .register-number-input-element.label-left label {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .register-number-input-element.label-left input {
    flex: 1;
    min-width: 0;
    max-width: 100%;
    width: 0;
  }

  input {
    font-size: 11px;
    line-height: 16px;
  }

  input::placeholder {
    font-size: 10px;
    opacity: 0.6;
  }
</style>
